/**
 * Small Owner Edit PIN unlock dialog.
 */

export interface PinDialogResult {
  unlocked: boolean;
  cancelled: boolean;
}

/** Mount dialog and verify via provided unlock function. */
export async function requestOwnerEditUnlock(
  verify: (pin: string) => Promise<{ ok: boolean; error?: string }>,
): Promise<PinDialogResult> {
  const title = 'Owner Edit Mode';
  const hint =
    'Enter the Editor PIN to unlock World Editor on this device.';

  return new Promise((resolve) => {
    const backdrop = document.createElement('div');
    backdrop.className = 'wp-owner-pin-backdrop';
    backdrop.innerHTML = `
      <form class="wp-owner-pin-dialog" autocomplete="off">
        <h2 class="wp-owner-pin-title">${title}</h2>
        <p class="wp-owner-pin-hint">${hint}</p>
        <label class="wp-owner-pin-label">
          <span>Editor PIN</span>
          <input type="password" name="pin" inputmode="numeric" enterkeyhint="go" autocomplete="off" />
        </label>
        <p class="wp-owner-pin-error" hidden></p>
        <div class="wp-owner-pin-actions">
          <button type="button" data-action="cancel">Cancel</button>
          <button type="submit" data-action="unlock">Unlock</button>
        </div>
      </form>
    `;
    const form = backdrop.querySelector('form')!;
    const input = backdrop.querySelector<HTMLInputElement>('input[name="pin"]')!;
    const errorEl = backdrop.querySelector<HTMLElement>('.wp-owner-pin-error')!;
    const cancelBtn = backdrop.querySelector<HTMLButtonElement>('[data-action="cancel"]')!;
    const unlockBtn = backdrop.querySelector<HTMLButtonElement>('[data-action="unlock"]')!;

    const finish = (result: PinDialogResult) => {
      backdrop.remove();
      resolve(result);
    };

    cancelBtn.addEventListener('click', () =>
      finish({ unlocked: false, cancelled: true }),
    );

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      errorEl.hidden = true;
      unlockBtn.disabled = true;
      cancelBtn.disabled = true;
      try {
        const result = await verify(input.value);
        if (result.ok) {
          input.value = '';
          finish({ unlocked: true, cancelled: false });
        } else {
          errorEl.textContent = result.error ?? 'Unlock failed';
          errorEl.hidden = false;
          input.select();
        }
      } finally {
        unlockBtn.disabled = false;
        cancelBtn.disabled = false;
      }
    });

    document.body.append(backdrop);
    ensurePinDialogStyles();
    input.focus();
  });
}

function ensurePinDialogStyles(): void {
  if (document.getElementById('wp-owner-pin-styles')) return;
  const style = document.createElement('style');
  style.id = 'wp-owner-pin-styles';
  style.textContent = `
    .wp-owner-pin-backdrop {
      position: fixed; inset: 0; z-index: 10000;
      display: grid; place-items: center;
      padding: max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
      background: rgba(15, 23, 42, 0.55);
      backdrop-filter: blur(4px);
      font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
    }
    .wp-owner-pin-dialog {
      width: min(360px, 100%);
      background: #0f172a;
      color: #e2e8f0;
      border: 1px solid rgba(148, 163, 184, 0.35);
      border-radius: 14px;
      padding: 18px 16px 14px;
      box-shadow: 0 18px 50px rgba(0,0,0,0.45);
    }
    .wp-owner-pin-title { margin: 0 0 6px; font-size: 18px; font-weight: 700; }
    .wp-owner-pin-hint { margin: 0 0 14px; font-size: 13px; line-height: 1.4; color: #94a3b8; }
    .wp-owner-pin-label { display: grid; gap: 6px; font-size: 12px; font-weight: 600; color: #cbd5e1; }
    .wp-owner-pin-label input {
      appearance: none; border-radius: 10px; border: 1px solid rgba(148,163,184,0.45);
      background: #111827; color: #f8fafc; padding: 12px 12px; font-size: 16px;
    }
    .wp-owner-pin-error { margin: 10px 0 0; color: #fca5a5; font-size: 13px; }
    .wp-owner-pin-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }
    .wp-owner-pin-actions button {
      appearance: none; border-radius: 10px; border: 1px solid rgba(148,163,184,0.45);
      background: #111827; color: #e2e8f0; padding: 10px 14px; font-size: 14px; font-weight: 600;
      min-height: 44px; cursor: pointer;
    }
    .wp-owner-pin-actions button[data-action="unlock"] {
      background: #2563eb; border-color: transparent; color: #fff;
    }
    .wp-owner-pin-actions button:disabled { opacity: 0.6; cursor: default; }
  `;
  document.head.append(style);
}
